import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { cjNav } from "../lib/cj";
import { CATEGORY_IMAGES, CATEGORY_FALLBACK } from "../config";

// Compact, deduplicated collection strip. The 48 canonical handles ARE the merged
// (Shopify + CJ) taxonomy — Shopify collections map onto these canonicals, so this
// list is duplicate-free by construction. Tiles open the hybrid collection page.
export const HomeCollectionStrip = () => {
  const [cols, setCols] = useState([]);
  const ref = useRef(null);
  useEffect(() => {
    let active = true;
    cjNav().then((d) => {
      if (!active) return;
      const seen = new Set();
      const uniq = (d.collections || []).filter((c) => {
        if (!c.handle || seen.has(c.handle)) return false;
        seen.add(c.handle); return true;
      });
      setCols(uniq.slice(0, 18));
    }).catch(() => {});
    return () => { active = false; };
  }, []);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 500, behavior: "smooth" });
  if (!cols.length) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 pt-8" data-testid="home-collection-strip">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg md:text-xl font-black font-display tracking-tight">Shop by Collection</h2>
        <div className="flex items-center gap-2">
          <Link to="/shop" data-testid="collections-view-all" className="text-xs font-bold uppercase tracking-wider text-taven-gold hover:underline flex items-center gap-1">View All <ArrowRight className="w-3.5 h-3.5" /></Link>
          <button onClick={() => scroll(-1)} className="p-1.5 rounded-full border border-black/10 hover:bg-taven-gray" aria-label="Scroll left"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => scroll(1)} className="p-1.5 rounded-full border border-black/10 hover:bg-taven-gray" aria-label="Scroll right"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-3 overflow-x-auto pb-2 no-scrollbar" style={{ scrollbarWidth: "none" }}>
        {cols.map((c) => (
          <Link key={c.handle} to={`/market/collection/${c.handle}`} data-testid={`collection-tile-${c.handle}`}
            className="w-[130px] sm:w-[150px] shrink-0 group">
            <div className="aspect-square rounded-lg overflow-hidden relative bg-taven-gray">
              <img src={c.image || CATEGORY_IMAGES[c.handle] || CATEGORY_FALLBACK} alt={c.name} loading="lazy"
                onError={(e) => { e.currentTarget.src = CATEGORY_IMAGES[c.handle] || CATEGORY_FALLBACK; }}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <p className="absolute bottom-2 left-2 right-2 text-white text-xs font-bold font-display leading-tight line-clamp-2">{c.name}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default HomeCollectionStrip;
